import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/Toast';
import { ordersAPI } from '@/lib/api';

/**
 * Notificador global de Pedidos WhatsApp.
 * - Faz polling em /api/whatsapp/orders a cada 12s enquanto o usuário está logado.
 * - Quando um novo pedido aparece, toca um "ding" forte (Web Audio API) e
 *   exibe um toast — independente de qual página o usuário esteja.
 * - Usuário pode silenciar via localStorage.setItem('wa_orders_sound', 'off').
 */
export default function WhatsAppOrderNotifier() {
  const { user } = useAuth();
  const toast = useToast();
  const lastSeenIdsRef = useRef(new Set());
  const initializedRef = useRef(false);
  const [muted, setMuted] = useState(
    typeof window !== 'undefined' && localStorage.getItem('wa_orders_sound') === 'off'
  );

  // Toca um "ding" agradável e perceptível usando Web Audio API.
  const playDing = () => {
    if (muted) return;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();

      const playTone = (freq, startAt, duration) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, ctx.currentTime + startAt);
        gain.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + startAt + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + startAt + duration);
        osc.connect(gain).connect(ctx.destination);
        osc.start(ctx.currentTime + startAt);
        osc.stop(ctx.currentTime + startAt + duration + 0.05);
      };

      // "Bi-dim-dim": dois tons agudos seguidos (igual notificação de caixa)
      playTone(880, 0.0, 0.18);
      playTone(1320, 0.18, 0.22);
      playTone(1760, 0.42, 0.28);

      // Fecha contexto depois para liberar recursos
      setTimeout(() => { try { ctx.close(); } catch (_e) { /* noop */ } }, 1200);
    } catch (e) {
      // silencioso
    }
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    localStorage.setItem('wa_orders_sound', next ? 'off' : 'on');
    if (!next) {
      // Toca um som de confirmação quando reativa
      playDing();
      toast.success('🔔 Som de pedidos WhatsApp ativado');
    } else {
      toast.info('🔕 Som de pedidos WhatsApp silenciado');
    }
  };

  useEffect(() => {
    // Expor o toggle globalmente para a sidebar/topbar acionar
    window.__waOrdersToggleSound = toggleMute;
    window.__waOrdersSoundMuted = () => muted;
    return () => {
      delete window.__waOrdersToggleSound;
      delete window.__waOrdersSoundMuted;
    };
  }, [muted]);

  useEffect(() => {
    if (!user) {
      lastSeenIdsRef.current = new Set();
      initializedRef.current = false;
      return;
    }

    let cancelled = false;

    const poll = async () => {
      try {
        const res = await ordersAPI.getWhatsAppOrders('pendente');
        if (cancelled) return;
        const orders = res?.data?.orders || res?.data || [];
        const ids = new Set(orders.map((o) => o.id).filter(Boolean));

        if (!initializedRef.current) {
          // Primeira chamada: só "memoriza" o estado, não toca nada
          lastSeenIdsRef.current = ids;
          initializedRef.current = true;
          return;
        }

        // Conta pedidos cujo id NÃO estava no snapshot anterior
        const novos = [...ids].filter((id) => !lastSeenIdsRef.current.has(id));
        if (novos.length > 0) {
          playDing();
          const detalhe = novos
            .map((id) => orders.find((o) => o.id === id))
            .filter(Boolean)
            .slice(0, 3)
            .map((o) => `#${o.order_number || (o.id || '').slice(0, 6).toUpperCase()} ${o.customer_name || ''}`)
            .join(' • ');
          toast.success(`🔔 ${novos.length} novo(s) pedido(s) WhatsApp! ${detalhe}`);
        }

        lastSeenIdsRef.current = ids;
      } catch (e) {
        // ignora erros (rede, logout, etc.)
      }
    };

    poll();
    const interval = setInterval(poll, 12000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [user]);

  return null;
}
